import { createFileRoute } from '@tanstack/react-router'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, DefList, C, Pre, H3 } from '../components/docs/prose'

export const Route = createFileRoute('/docs/ai-insights')({ component: AiInsights })

const SECTIONS = [
  { id: 'what-it-does', title: 'What it does' },
  { id: 'sent', title: 'What is sent' },
  { id: 'not-sent', title: 'What is not sent' },
  { id: 'contract', title: 'The descriptive-only contract' },
  { id: 'quota', title: 'Quota and your own key' },
]

function AiInsights() {
  return (
    <DocPage
      path="/docs/ai-insights"
      title="AI insights"
      lede={
        <>
          One feature, narrowly scoped: a short read of your bill history. This
          page says exactly which numbers leave the server, which never do, and
          how often a request can be made.
        </>
      }
      sections={SECTIONS}
    >
      <Section id="what-it-does" title="What it does">
        <P>
          FolioWorth summarises twelve months of bill payments and asks a
          language model what stands out. You get a one-sentence summary and at
          most a handful of observations, each tagged <C>info</C>,{' '}
          <C>watch</C> or <C>action</C> — the last reserved for money actually
          being lost or a payment that appears to have gone missing.
        </P>
        <P>
          It never runs on page load. Opening the page reads whatever was
          generated last; a model call happens only when you ask for a refresh.
          And a refresh where nothing has changed does not call the model
          either: the facts are hashed, and an identical hash returns the
          cached answer with a note saying the figures are unchanged.
        </P>
      </Section>

      <Section id="sent" title="What is sent">
        <P>
          The prompt is built from aggregates. This is the complete list —
          there is no other payload:
        </P>
        <DefList
          items={[
            ['Totals', 'Total billed, total paid, and outstanding, over twelve months.'],
            ['Revolving', 'How many bills carry a balance, and the total amount revolving.'],
            ['Unposted count', 'How many bills were marked paid but never reconciled.'],
            ['Monthly totals', 'Twelve numbers: what was paid each month, oldest first.'],
            ['Top payees', 'At most twenty, by average amount. For each: the payee label, its category, the average amount, the first and last amounts seen, the percentage change between them, and how many months it was missed.'],
            ['Currency and window', 'CAD, twelve months.'],
          ]}
        />
        <P>
          Payee labels do leave the server. They are text you typed, or a
          suggestion you accepted — &ldquo;Hydro One&rdquo;, &ldquo;Amex
          Plat&rdquo;. If that is more than you want shared, rename them before
          enabling the feature, or leave it off.
        </P>
      </Section>

      <Section id="not-sent" title="What is not sent">
        <DefList
          items={[
            ['No transactions', 'Not one row. The model never sees an individual payment.'],
            ['No account numbers or names', 'Neither yours nor the payee’s.'],
            ['No merchant data from your bank feed', 'Nothing Plaid returned about counterparties is included.'],
            ['No dates', 'Months are positions in a list, not calendar dates.'],
            ['No identity', 'No user id, no household id, no email, no name.'],
            ['Nothing outside bills', 'No net worth, no holdings, no balances, no budgets, no goals.'],
          ]}
        />
        <P>
          The feature is off by default. Nothing is sent anywhere until you
          turn it on.
        </P>
      </Section>

      <Section id="contract" title="The descriptive-only contract">
        <P>
          The system prompt forbids advice, and the response is validated
          rather than trusted:
        </P>
        <Pre>{`Describe only what the figures show. Never invent a number, payee, or date.
Do NOT give financial, investment, tax or legal advice. Do not tell the user
what they should buy, sell, refinance, or invest in. Observations only.
If nothing is notable, say so rather than padding.`}</Pre>
        <H3>What is enforced on the way back</H3>
        <P>
          Every stored payload is stamped{' '}
          <C>analysis_type: descriptive_only</C> by FolioWorth, not by the
          model — the model cannot claim to be doing something else. Severity
          values outside <C>info</C>, <C>watch</C> and <C>action</C> are
          rewritten to <C>info</C>, and the list is truncated to five. A reply
          that arrives cut off is salvaged only as a prefix of what the model
          actually said; nothing is completed on its behalf.
        </P>
        <P>
          The temperature is zero. This is a reading of your figures, not a
          creative exercise, and the same figures should produce the same
          reading.
        </P>
      </Section>

      <Section id="quota" title="Quota and your own key">
        <P>
          The default runs on a shared key, which is a fixed budget spread
          across every household, so it is capped:
        </P>
        <DefList
          items={[
            ['Shared key', 'Four model calls per household per rolling twenty-four hours.'],
            ['Your own key', 'Sixty per rolling twenty-four hours, and none of it charged against the shared pool.'],
          ]}
        />
        <P>
          Rolling, not calendar: the window is the last twenty-four hours from
          now, so a burst at midnight does not buy a second allowance at 12:01.
          Calls are counted in an append-only log kept separately from the
          insights themselves, so deleting an insight and asking again does not
          reset anything.
        </P>
        <P>
          A key you supply is encrypted at rest with the same AES-256-GCM
          scheme used for bank tokens, and is never shown again — the settings
          page displays the last four characters only. The default provider is
          NVIDIA&rsquo;s OpenAI-compatible endpoint running{' '}
          <C>meta/llama-3.1-8b-instruct</C>.
        </P>
      </Section>
    </DocPage>
  )
}
